import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CardActions,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Grid,
  Divider,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Save as SaveIcon,
  Send as SendIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  AttachFile as AttachmentIcon,
  Comment as CommentIcon,
  FindInPage as FindingIcon,
  BugReport as DeficiencyIcon,
  Healing as RemediationIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import DocumentViewer from '../../../components/audit/DocumentViewer';

// Tab panel component for the review interface
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`review-tabpanel-${index}`}
      aria-labelledby={`review-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ReviewInterface = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [auditCase, setAuditCase] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [currentAnswerIndex, setCurrentAnswerIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [findings, setFindings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [review, setReview] = useState({
    comment: '',
    findings: [],
    relates_to_engagements: false,
    has_deficiency: false,
    deficiency_severity: 'medium',
    root_cause: [],
    remedial_action: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Root cause options (these would typically come from the API)
  const rootCauseOptions = [
    { id: 'training', label: 'Insufficient Training' },
    { id: 'oversight', label: 'Lack of Oversight' },
    { id: 'procedures', label: 'Inadequate Procedures' },
    { id: 'resources', label: 'Insufficient Resources' },
    { id: 'communication', label: 'Poor Communication' },
    { id: 'technology', label: 'Technology Issues' }
  ];

  // Fetch case, answers, findings, and documents on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch case details
        const caseResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/cases/${caseId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        console.log('Case response:', caseResponse.data);
        setAuditCase(caseResponse.data);
        
        // Fetch answers for this case
        const answersResponse = await axios.get(`${process.env.REACT_APP_API_URL}/responses/${caseId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        

        // Check if the response has the expected structure
        if (answersResponse.data && answersResponse.data.success && answersResponse.data.data) {
          // Extract the answers from the response data
          const responseData = answersResponse.data.data;
          
          // Check if we have responses and if they have answers
          if (responseData.length > 0 && responseData[0].answers) {
            // Flatten the answers from all responses
            const allAnswers = [];
            responseData.forEach(response => {
              response.answers.forEach(answer => {
                // Add question information to each answer
                allAnswers.push({
                  ...answer,
                  question_type: answer.question ? answer.question.type : 'text',
                  question_text: answer.question ? answer.question.description : 'Unknown Question',
                  review_guidelines: answer.question ? answer.question.review_guidelines : '',
                  value: answer.answer_text || answer.answer_options || '',
                  // Make sure file_data is included in the processed answer
                  file_data: answer.file_data || null
                });
              });
            });
            
            console.log('Processed answers:', allAnswers);
            setAnswers(allAnswers);
          } else {
            console.log('No answers found in response data');
            setAnswers([]);
          }
        } else {
          console.log('Unexpected response structure:', answersResponse.data);
          setAnswers([]);
        }
        
        // Fetch documents for this case
        const documentsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/documents?case_id=${caseId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        setDocuments(documentsResponse.data);
        
        // Fetch existing review for the current answer if it exists
        if (answersResponse.data.length > 0) {
          fetchReviewForAnswer(answersResponse.data[0].id);
          fetchFindingsForQuestion(answersResponse.data[0].question_id);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load review data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [caseId]);

  // Fetch findings for a specific question
  const fetchFindingsForQuestion = async (questionId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/audit/findings?question_id=${questionId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setFindings(response.data);
    } catch (err) {
      console.error('Error fetching findings:', err);
      // Don't set error state here to avoid disrupting the main UI
    }
  };

  // Fetch review for a specific answer
  const fetchReviewForAnswer = async (answerId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/audit/reviews?answer_id=${answerId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      // If a review exists for this answer, load it
      if (response.data.length > 0) {
        const existingReview = response.data[0];
        setReview({
          comment: existingReview.comment || '',
          findings: existingReview.findings || [],
          relates_to_engagements: existingReview.relates_to_engagements || false,
          has_deficiency: existingReview.has_deficiency || false,
          deficiency_severity: existingReview.deficiency_severity || 'medium',
          root_cause: existingReview.root_cause || [],
          remedial_action: existingReview.remedial_action || ''
        });
      } else {
        // Reset review form for a new review
        setReview({
          comment: '',
          findings: [],
          relates_to_engagements: false,
          has_deficiency: false,
          deficiency_severity: 'medium',
          root_cause: [],
          remedial_action: ''
        });
      }
    } catch (err) {
      console.error('Error fetching review:', err);
      // Don't set error state here to avoid disrupting the main UI
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    
    setReview(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle multi-select changes (findings, root causes)
  const handleMultiSelectChange = (e) => {
    const { name, value } = e.target;
    setReview(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle document selection
  const handleDocumentSelect = (document) => {
    setSelectedDocument(document);
    setShowDocumentViewer(true);
  };

  // Handle navigation to previous answer
  const handlePrevAnswer = async () => {
    // Save current review before navigating
    await saveReview();
    
    if (currentAnswerIndex > 0) {
      const newIndex = currentAnswerIndex - 1;
      setCurrentAnswerIndex(newIndex);
      
      // Load review and findings for the new answer
      fetchReviewForAnswer(answers[newIndex].id);
      fetchFindingsForQuestion(answers[newIndex].question_id);
    }
  };

  // Handle navigation to next answer
  const handleNextAnswer = async () => {
    // Save current review before navigating
    await saveReview();
    
    if (currentAnswerIndex < answers.length - 1) {
      const newIndex = currentAnswerIndex + 1;
      setCurrentAnswerIndex(newIndex);
      
      // Load review and findings for the new answer
      fetchReviewForAnswer(answers[newIndex].id);
      fetchFindingsForQuestion(answers[newIndex].question_id);
    }
  };

  // Save the current review
  const saveReview = async () => {
    if (!answers.length) return;
    
    const currentAnswer = answers[currentAnswerIndex];
    
    try {
      const reviewData = {
        case_id: caseId,
        answer_id: currentAnswer.id,
        review_type: auditCase.first_reviewer_id === parseInt(localStorage.getItem('user_id')) ? 'first' : 'second',
        ...review
      };
      
      await axios.post(`${process.env.REACT_APP_API_URL}/audit/reviews`, reviewData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setSnackbar({
        open: true,
        message: 'Review saved successfully',
        severity: 'success'
      });
      
      return true;
    } catch (err) {
      console.error('Error saving review:', err);
      setSnackbar({
        open: true,
        message: 'Error saving review. Please try again.',
        severity: 'error'
      });
      
      return false;
    }
  };

  // Submit the complete review
  const submitReview = async () => {
    // First save the current review
    const saveResult = await saveReview();
    
    if (!saveResult) return;
    
    if (window.confirm('Are you sure you want to submit your review? This will finalize your assessment and notify the next reviewer or manager.')) {
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/audit/cases/${caseId}/review`, {}, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        setSnackbar({
          open: true,
          message: 'Review submitted successfully',
          severity: 'success'
        });
        
        // Navigate back to the dashboard or case list
        setTimeout(() => {
          navigate('/audit/my-cases');
        }, 2000);
      } catch (err) {
        console.error('Error submitting review:', err);
        setSnackbar({
          open: true,
          message: err.response?.data?.message || 'Error submitting review. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  // Get answer documents
  const getAnswerDocuments = () => {
    if (!answers.length) return [];
    
    const currentAnswer = answers[currentAnswerIndex];
    
    // First check if the answer has file_data (new format)
    const fileData = getFileDataFromAnswer(currentAnswer);
    if (fileData.length > 0) {
      return fileData;
    }
    
    // Fall back to the old method if no file_data
    return documents.filter(doc => doc.answer_id === currentAnswer.id);
  };

  // Format answer value based on question type
  const formatAnswerValue = (answer) => {
    if (!answer) return 'No response';
    
    const { question_type, value } = answer;
    
    switch (question_type) {
      case 'file':
      case 'upload':
      case 'document':
        // For file questions, we'll return null and render a custom component
        return null;
      case 'radio':
        return value;
      case 'checkbox':
        return Array.isArray(value) ? value.join(', ') : value;
      case 'date':
        return new Date(value).toLocaleDateString();
      default:
        return value;
    }
  };
  
  // Render file list for a file-type question
  const renderFileList = (answer) => {
    if (!answer) return null;
    
    const { question_type } = answer;
    
    if (!['file', 'upload', 'document'].includes(question_type)) {
      return null;
    }
    
    const files = getFileDataFromAnswer(answer);
    
    if (files.length === 0) {
      return <Typography variant="body2" color="text.secondary">No files uploaded</Typography>;
    }
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {files.map(file => (
          <Paper 
            key={file.id} 
            variant="outlined" 
            sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AttachmentIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
              <Typography 
                variant="body1" 
                component="span" 
                sx={{ 
                  fontWeight: 'medium',
                  cursor: 'pointer', 
                  '&:hover': { textDecoration: 'underline', color: 'primary.main' } 
                }}
                onClick={() => handleDocumentSelect(file)}
              >
                {file.filename || file.original_filename}
              </Typography>
            </Box>
            
            <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5 }}>
              {file.mime_type || 'Unknown file type'}
              {file.file_size && ` | ${(file.file_size / 1024).toFixed(2)} KB`}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, ml: 3.5 }}>
              {file.preview_url && (
                <Button 
                  size="small" 
                  variant="outlined" 
                  startIcon={<OpenInNewIcon />}
                  onClick={() => handleDocumentSelect(file)}
                >
                  Preview
                </Button>
              )}
              {file.download_url && (
                <Button 
                  size="small" 
                  variant="outlined" 
                  startIcon={<DownloadIcon />}
                  onClick={() => window.open(file.download_url, '_blank')}
                >
                  Download
                </Button>
              )}
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };
  
  // Get file data from answer
  const getFileDataFromAnswer = (answer) => {
    if (!answer) return [];
    
    console.log('Checking file data for answer:', answer);
    
    // If the answer has file_data, parse it
    if (answer.file_data) {
      console.log('Found file_data:', answer.file_data);
      try {
        // Handle both string and already parsed object
        const parsedData = typeof answer.file_data === 'string' 
          ? JSON.parse(answer.file_data)
          : answer.file_data;
        
        console.log('Parsed file data:', parsedData);
        return Array.isArray(parsedData) ? parsedData : [];
      } catch (e) {
        console.error('Error parsing file data:', e);
        return [];
      }
    } else {
      console.log('No file_data found in answer');
    }
    
    // Check for documents in the documents array that are linked to this answer
    if (documents.length > 0 && answer.id) {
      const linkedDocs = documents.filter(doc => doc.answer_id === answer.id);
      if (linkedDocs.length > 0) {
        console.log('Found linked documents:', linkedDocs);
        return linkedDocs.map(doc => ({
          id: doc.id,
          filename: doc.original_filename,
          file_size: doc.file_size,
          mime_type: doc.mime_type,
          preview_url: `${process.env.REACT_APP_API_URL}/documents/${doc.id}/preview`,
          download_url: `${process.env.REACT_APP_API_URL}/documents/${doc.id}/download`
        }));
      }
    }
    
    return [];
  };

  // Render the current answer and review form
  const renderReviewForm = () => {
    if (!answers.length) return <Typography>No answers to review</Typography>;
    
    const currentAnswer = answers[currentAnswerIndex];
    const answerDocuments = getAnswerDocuments();
    console.log('Current answer:', currentAnswer);
    console.log('Answer documents:', answerDocuments);
    
    return (
      <Box>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">
              Question {currentAnswerIndex + 1} of {answers.length}
            </Typography>
            <Box>
              <Button 
                onClick={handlePrevAnswer} 
                disabled={currentAnswerIndex === 0}
                startIcon={<BackIcon />}
                sx={{ mr: 1 }}
              >
                Previous
              </Button>
              <Button 
                onClick={handleNextAnswer} 
                disabled={currentAnswerIndex === answers.length - 1}
                endIcon={<NextIcon />}
              >
                Next
              </Button>
            </Box>
          </Box>
          
          <Typography variant="subtitle1" fontWeight="bold">
            {currentAnswer.question_text}
          </Typography>
          
          <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Client Response:
            </Typography>
            {/* For file-type questions, render the file list */}
            {['file', 'upload', 'document'].includes(currentAnswer.question_type) ? (
              renderFileList(currentAnswer)
            ) : (
              <Typography variant="body1">
                {formatAnswerValue(currentAnswer)}
              </Typography>
            )}
          </Box>

          <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Review Guidelines:
            </Typography>
            <Typography variant="body1">
              {currentAnswer.review_guidelines}
            </Typography>
          </Box>

          
          {/* We're now showing files directly in the client response section */}
        </Paper>
        
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="review tabs">
            <Tab icon={<CommentIcon />} label="Comments" id="review-tab-0" />
            <Tab icon={<FindingIcon />} label="Findings" id="review-tab-1" />
            <Tab icon={<DeficiencyIcon />} label="Deficiencies" id="review-tab-2" />
            <Tab icon={<RemediationIcon />} label="Remediation" id="review-tab-3" />
          </Tabs>
          
          <TabPanel value={tabValue} index={0}>
            <TextField
              fullWidth
              multiline
              rows={4}
              name="comment"
              label="Review Comments"
              value={review.comment}
              onChange={handleInputChange}
              placeholder="Enter your comments about the client's response..."
            />
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <FormControl fullWidth>
              <InputLabel>Select Findings</InputLabel>
              <Select
                multiple
                name="findings"
                value={review.findings}
                onChange={handleMultiSelectChange}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const finding = findings.find(f => f.id === value);
                      return (
                        <Chip 
                          key={value} 
                          label={finding ? finding.description : value} 
                          size="small" 
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {findings.map((finding) => (
                  <MenuItem key={finding.id} value={finding.id}>
                    {finding.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Checkbox
                  name="relates_to_engagements"
                  checked={review.relates_to_engagements}
                  onChange={handleInputChange}
                />
              }
              label="Relates to Engagements"
              sx={{ mt: 2 }}
            />
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <FormControlLabel
              control={
                <Checkbox
                  name="has_deficiency"
                  checked={review.has_deficiency}
                  onChange={handleInputChange}
                />
              }
              label="Deficiency Identified"
            />
            
            {review.has_deficiency && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Deficiency Severity</InputLabel>
                <Select
                  name="deficiency_severity"
                  value={review.deficiency_severity}
                  onChange={handleInputChange}
                  label="Deficiency Severity"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            )}
          </TabPanel>
          
          <TabPanel value={tabValue} index={3}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Root Cause Analysis</InputLabel>
              <Select
                multiple
                name="root_cause"
                value={review.root_cause}
                onChange={handleMultiSelectChange}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const cause = rootCauseOptions.find(c => c.id === value);
                      return (
                        <Chip 
                          key={value} 
                          label={cause ? cause.label : value} 
                          size="small" 
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {rootCauseOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              name="remedial_action"
              label="Remedial Action"
              value={review.remedial_action}
              onChange={handleInputChange}
              placeholder="Describe the recommended remedial action..."
            />
          </TabPanel>
        </Paper>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<SaveIcon />}
            onClick={saveReview}
          >
            Save Review
          </Button>
          
          <Button 
            variant="contained" 
            color="primary" 
            endIcon={<SendIcon />}
            onClick={submitReview}
          >
            Submit Complete Review
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h1">
              Review: {auditCase?.title}
            </Typography>
            <Chip 
              label={auditCase?.status.replace('_', ' ').toUpperCase()} 
              color="primary" 
            />
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {renderReviewForm()}
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Case Details
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Firm:</strong> {auditCase?.firm?.name}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Review Period:</strong> {new Date(auditCase?.review_period_start).toLocaleDateString()} - {new Date(auditCase?.review_period_end).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Manager:</strong> {auditCase?.manager?.name}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>First Reviewer:</strong> {auditCase?.first_reviewer?.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Second Reviewer:</strong> {auditCase?.second_reviewer?.name}
                </Typography>
              </Paper>
              
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  All Documents
                </Typography>
                {documents.length === 0 ? (
                  <Typography variant="body2">No documents uploaded</Typography>
                ) : (
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {documents.map(doc => (
                      <Box 
                        key={doc.id} 
                        sx={{ 
                          p: 1, 
                          borderBottom: '1px solid #eee',
                          '&:hover': { bgcolor: 'action.hover', cursor: 'pointer' }
                        }}
                        onClick={() => handleDocumentSelect(doc)}
                      >
                        <Typography variant="body2">
                          <AttachmentIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                          {doc.original_filename}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
          
          {/* Document Viewer Dialog */}
          {showDocumentViewer && selectedDocument && (
            <DocumentViewer 
              document={selectedDocument} 
              open={showDocumentViewer}
              onClose={() => setShowDocumentViewer(false)}
            />
          )}
        </Box>
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewInterface;
